import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Building2, Home, LockKeyhole, RotateCcw, UserRound } from 'lucide-react';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { ConfigurationModule } from './configuration-module';
import { loadConfigurationDataset, type ConfigurationPortalAccess } from '@/app/utils/aql-configuration-supabase';
import { supabase } from '@/utils/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { AqlVerticalLogo, AqlVisionMark } from '../branding/aql-vision-brand';
import { AqlVisionLoader } from '../branding/aql-vision-loader';

type PortalMode = 'admin' | 'client';
type SubmitAction = 'login' | 'reset-link' | 'password-reset' | null;

interface AccessState extends ConfigurationPortalAccess {}
interface ConfigurationPortalProps {
  forcedMode?: PortalMode;
}

const normalize = (value: string) => value.trim().toLowerCase();
const slugify = (value: string) =>
  normalize(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const slugSegments = (value: unknown) =>
  String(value ?? '')
    .split(/[/?#]+/)
    .map((segment) => slugify(segment))
    .filter(Boolean);

const projectMatchesRoute = (project: { slug?: string | null; name?: string | null }, projectSlug: string, clientSlug: string) => {
  const rawSlug = String(project.slug ?? '');
  const slugParts = slugSegments(rawSlug);
  const directSlug = slugify(rawSlug);
  const nameSlug = slugify(String(project.name ?? ''));
  const normalizedProjectSlug = slugify(projectSlug);
  const normalizedClientSlug = slugify(clientSlug);

  return (
    directSlug === normalizedProjectSlug ||
    directSlug === `${normalizedClientSlug}-${normalizedProjectSlug}` ||
    slugParts[slugParts.length - 1] === normalizedProjectSlug ||
    (slugParts[0] === normalizedClientSlug && slugParts[slugParts.length - 1] === normalizedProjectSlug) ||
    nameSlug === normalizedProjectSlug
  );
};
const RECOVERY_STORAGE_KEY = 'aql_config_recovery_pending';
const CLIENT_ADMIN_ROLE = 'client_admin';
const PASSWORD_SETUP_LINK_TYPES = new Set(['recovery', 'invite']);
const debugLog = (label: string, value: unknown) => {
  try {
    console.log(label, JSON.stringify(value, null, 2));
  } catch {
    console.log(label, value);
  }
};
const inferPathScope = (pathname: string, hash: string) => {
  const hashPath = hash.startsWith('#/') ? hash.slice(1) : '';
  const path = hashPath || pathname;
  const segments = path.split('/').filter(Boolean);
  const clientSlug = segments[0] && segments[0] !== 'admin' ? segments[0] : null;

  return {
    clientSlug,
    projectSlug: clientSlug ? segments[1] ?? null : null
  };
};
const rolePriority = (role: string | null | undefined) => {
  if (role === 'system_administrator' || role === 'admin') return 0;
  if (role === CLIENT_ADMIN_ROLE) return 1;
  return 2;
};
const isGlobalAdminRole = (role: string | null | undefined) => role === 'system_administrator' || role === 'admin';
const profileHasClientAccess = (profile: { client_id?: string | null; client_ids?: string[] }, clientId: string) =>
  profile.client_id === clientId || (profile.client_ids ?? []).includes(clientId);
const getAuthRedirectUrl = () => {
  if (typeof window === 'undefined') return undefined;
  if (!window.location.origin || window.location.origin === 'null') return undefined;
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
};
const commonEmailDomainFixes: Record<string, string> = {
  'gmail.cm': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'outlook.cm': 'outlook.com'
};

const getEmailDomainSuggestion = (value: string) => {
  const [, domain = ''] = value.trim().toLowerCase().split('@');
  const suggestion = commonEmailDomainFixes[domain];
  if (!suggestion) return null;
  return value.trim().replace(new RegExp(`${domain}$`, 'i'), suggestion);
};

const withPortalTimeout = async <T,>(promise: Promise<T>, message: string, timeoutMs = 18000): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export function ConfigurationPortal({ forcedMode }: ConfigurationPortalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ clientSlug?: string; projectSlug?: string }>();
  const pathScope = useMemo(() => inferPathScope(location.pathname, location.hash), [location.hash, location.pathname]);
  const clientSlug = forcedMode === 'admin' ? null : params.clientSlug ?? pathScope.clientSlug;
  const projectSlug = params.projectSlug ?? pathScope.projectSlug;
  const mode: PortalMode = forcedMode ?? (clientSlug ? 'client' : 'admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [submitAction, setSubmitAction] = useState<SubmitAction>(null);
  const [access, setAccess] = useState<AccessState | null>(null);
  const [clientExists, setClientExists] = useState(true);
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);
  const submitting = submitAction !== null;

  const hasPasswordSetupToken = () => {
    if (typeof window === 'undefined') return false;
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(hash);
    return PASSWORD_SETUP_LINK_TYPES.has(params.get('type') ?? '');
  };

  const setRecoveryPending = (pending: boolean) => {
    if (typeof window === 'undefined') return;
    if (pending) {
      window.sessionStorage.setItem(RECOVERY_STORAGE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(RECOVERY_STORAGE_KEY);
    }
  };

  const hasRecoveryPending = () => {
    if (typeof window === 'undefined') return false;
    return hasPasswordSetupToken() || window.sessionStorage.getItem(RECOVERY_STORAGE_KEY) === 'true';
  };

  const resolvePortalAccess = async (
    session: Session | null,
    expectedSlug: string | null,
    expectedProjectSlug: string | null
  ): Promise<AccessState | null> => {
    if (!session?.user.email) return null;

    const result = await loadConfigurationDataset();
    const routeClient = expectedSlug
      ? result.data.clients.find((item) =>
        slugify(item.slug) === slugify(expectedSlug) || slugify(item.name) === slugify(expectedSlug)
      ) ?? null
      : null;

    if (expectedSlug) {
      setClientExists(Boolean(routeClient));
      if (!routeClient) {
        throw new Error('Este URL não corresponde a nenhum cliente configurado.');
      }
    }

    const profiles = result.data.users
      .filter((item) =>
        (item.auth_user_id && item.auth_user_id === session.user.id) ||
        normalize(item.email) === normalize(session.user.email ?? '')
      )
      .sort((left, right) => {
        if (routeClient) {
          const leftClientMatch = profileHasClientAccess(left, routeClient.client_id) ? 0 : 1;
          const rightClientMatch = profileHasClientAccess(right, routeClient.client_id) ? 0 : 1;
          if (leftClientMatch !== rightClientMatch) return leftClientMatch - rightClientMatch;
        } else {
          const leftAdmin = isGlobalAdminRole(left.role) ? 0 : 1;
          const rightAdmin = isGlobalAdminRole(right.role) ? 0 : 1;
          if (leftAdmin !== rightAdmin) return leftAdmin - rightAdmin;
        }

        const leftAuthMatch = left.auth_user_id === session.user.id ? 0 : 1;
        const rightAuthMatch = right.auth_user_id === session.user.id ? 0 : 1;
        if (leftAuthMatch !== rightAuthMatch) return leftAuthMatch - rightAuthMatch;

        const leftActive = left.is_active ? 0 : 1;
        const rightActive = right.is_active ? 0 : 1;
        if (leftActive !== rightActive) return leftActive - rightActive;

        const leftRole = rolePriority(left.role);
        const rightRole = rolePriority(right.role);
        if (leftRole !== rightRole) return leftRole - rightRole;

        return String(left.user_id).localeCompare(String(right.user_id));
      });

    debugLog('DEBUG: Matching access profiles =', profiles.map((item) => ({
      userId: item.user_id,
      authUserId: item.auth_user_id,
      email: item.email,
      role: item.role,
      isActive: item.is_active,
      clientId: item.client_id,
      clientIds: item.client_ids ?? [],
      projectId: item.project_id,
      routeClientId: routeClient?.client_id ?? null,
      clientMatch: routeClient ? profileHasClientAccess(item, routeClient.client_id) : null
    })));

    const profile = routeClient
      ? profiles.find((item) => item.is_active && profileHasClientAccess(item, routeClient.client_id)) ?? null
      : profiles.find((item) => item.is_active && isGlobalAdminRole(item.role)) ?? null;

    if (!profile) {
      throw new Error(
        routeClient
          ? 'O utilizador autenticado não tem acesso ativo ao portal deste cliente.'
          : 'Este portal requer um utilizador com role admin ou system_administrator.'
      );
    }

    const role = profile.role;

    if (!expectedSlug) {
      return {
        email: profile.email,
        authUserId: session.user.id,
        accessToken: session.access_token,
        role,
        clientId: profile.client_id ?? null,
        mode: 'admin',
        clientSlug: null
      };
    }

    if (expectedProjectSlug) {
      const clientProject = result.data.projects.find((item) =>
        item.client_id === routeClient.client_id &&
        projectMatchesRoute(item, expectedProjectSlug, routeClient.slug || routeClient.name)
      );

      if (!clientProject) {
        throw new Error('Este URL não corresponde a nenhum projeto configurado para este cliente.');
      }
    }

    return {
      email: profile.email,
      authUserId: session.user.id,
      accessToken: session.access_token,
      role,
      clientId: routeClient.client_id,
      mode: expectedSlug ? 'client' : 'admin',
      clientSlug: expectedSlug
    };
  };

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      setChecking(true);
      setAccess(null);
      try {
        console.log('DEBUG: Starting bootstrap');
        debugLog('DEBUG: route scope =', {
          pathname: location.pathname,
          hash: location.hash,
          params,
          pathScope,
          clientSlug,
          projectSlug,
          mode
        });
        const recoveryFlow = hasRecoveryPending();
        console.log('DEBUG: recoveryFlow =', recoveryFlow);
        if (recoveryFlow && active) {
          setRecoveryPending(true);
          setIsRecoveryFlow(true);
          setAccess(null);
          setError(null);
        }

        console.log('DEBUG: Getting session from Supabase');
        const { data } = await supabase.auth.getSession();
        console.log('DEBUG: Session data =', data);
        if (!active) return;
        if (data.session && !recoveryFlow) {
          try {
            console.log('DEBUG: Resolving portal access');
            const nextAccess = await resolvePortalAccess(data.session, clientSlug, projectSlug);
            debugLog('DEBUG: Access resolved =', {
              mode: nextAccess?.mode,
              clientSlug: nextAccess?.clientSlug,
              clientId: nextAccess?.clientId,
              role: nextAccess?.role,
              email: nextAccess?.email
            });
            if (active) setAccess(nextAccess);
          } catch (accessError: any) {
            console.error('DEBUG: Access error =', accessError);
            if (active) {
              setError(accessError?.message ?? 'Não foi possível validar o acesso do utilizador autenticado.');
              setAccess(null);
            }
          }
        }

        console.log('DEBUG: Setting up auth listener');
        const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('DEBUG: Auth state change =', event, session);
          if (!active) return;
          if (event === 'PASSWORD_RECOVERY') {
            setRecoveryPending(true);
            setIsRecoveryFlow(true);
            setAccess(null);
            setError(null);
            setResetMessage(null);
            return;
          }
          if (hasRecoveryPending()) {
            setRecoveryPending(true);
            setIsRecoveryFlow(true);
            setAccess(null);
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
              return;
            }
          }
          if (!session) {
            setAccess(null);
            if (!hasRecoveryPending()) {
              setIsRecoveryFlow(false);
            }
            return;
          }
          if (hasRecoveryPending()) {
            setIsRecoveryFlow(true);
            return;
          }
          try {
            const nextAccess = await resolvePortalAccess(session, clientSlug, projectSlug);
            debugLog('DEBUG: Listener access resolved =', {
              event,
              mode: nextAccess?.mode,
              clientSlug: nextAccess?.clientSlug,
              clientId: nextAccess?.clientId,
              role: nextAccess?.role,
              email: nextAccess?.email
            });
            if (active) {
              setAccess(nextAccess);
              setError(null);
            }
          } catch (accessError: any) {
            console.error('DEBUG: Auth listener access error =', accessError);
            if (active) {
              setError(accessError?.message ?? 'Não foi possível validar a sessão Supabase.');
              setAccess(null);
            }
          }
        });

        return () => listener.subscription.unsubscribe();
      } catch (bootstrapError: any) {
        console.error('DEBUG: Bootstrap error =', bootstrapError);
        if (active) {
          setError(bootstrapError?.message ?? 'Erro no carregamento inicial.');
        }
      } finally {
        if (active) setChecking(false);
      }
    };

    let cleanup: (() => void) | undefined;
    bootstrap().then((dispose) => {
      cleanup = dispose;
    });
    return () => {
      active = false;
      cleanup?.();
    };
  }, [clientSlug, projectSlug]);

  const resolvedMode = access?.mode ?? mode;
  const resolvedClientSlug = access?.clientSlug ?? clientSlug;
  const accessConflictsWithClientRoute = Boolean(access && clientSlug && (access.mode !== 'client' || access.clientSlug !== clientSlug));
  const title = useMemo(() => (
    resolvedMode === 'admin' ? 'Administrador da plataforma' : `Portal do cliente${resolvedClientSlug ? ` / ${resolvedClientSlug}` : ''}`
  ), [resolvedClientSlug, resolvedMode]);

  if (accessConflictsWithClientRoute) {
    console.error('DEBUG: Blocking admin access on client URL', {
      routeClientSlug: clientSlug,
      routeProjectSlug: projectSlug,
      accessMode: access?.mode,
      accessClientSlug: access?.clientSlug,
      accessRole: access?.role,
      accessEmail: access?.email
    });
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-5 sm:px-6">
        <div className="w-full max-w-2xl rounded-[24px] border border-red-500/30 bg-card px-5 py-6 shadow-sm sm:px-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-red-700 dark:text-red-300">AQL Vision Config</p>
          <h1 className="mt-2 text-2xl font-semibold text-foreground">Contexto de acesso inconsistente</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Este URL é do cliente <span className="font-medium text-foreground">{clientSlug}</span>, mas a sessão tentou abrir outro contexto.
          </p>
          <Button
            onClick={async () => {
              await supabase.auth.signOut();
              setRecoveryPending(false);
              setAccess(null);
              setError(null);
              setIsRecoveryFlow(false);
            }}
            className="mt-5 rounded-[16px] px-5 py-3 text-sm font-semibold"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Revalidar sessão
          </Button>
        </div>
      </div>
    );
  }

  const handleLogin = async () => {
    setSubmitAction('login');
    setError(null);

    try {
      const { data, error: signInError } = await withPortalTimeout(
        supabase.auth.signInWithPassword({
          email: email.trim(),
          password
        }),
        'O login demorou mais do que o esperado. Verifica a ligação e tenta novamente.'
      );

      if (signInError) {
        setError(signInError.message);
        return;
      }

      const nextAccess = await withPortalTimeout(
        resolvePortalAccess(data.session, clientSlug, projectSlug),
        'A sessão foi aceite, mas a validação de acesso está a demorar. Tenta novamente dentro de instantes.'
      );
      setAccess(nextAccess);
    } catch (loginError: any) {
      setError(loginError?.message ?? 'Não foi possível validar o acesso ao portal de configuração.');
      setAccess(null);
    } finally {
      setSubmitAction(null);
    }
  };

  const handleRequestPasswordReset = async () => {
    const nextEmail = email.trim();
    if (!nextEmail) {
      setError('Indica o teu email para receberes o link de recuperação.');
      setResetMessage(null);
      return;
    }

    const suggestedEmail = getEmailDomainSuggestion(nextEmail);
    if (suggestedEmail) {
      setError(`Confirma o email: escreveste ${nextEmail}. Queres dizer ${suggestedEmail}?`);
      setResetMessage(null);
      return;
    }

    setSubmitAction('reset-link');
    setError(null);
    setResetMessage(null);

    try {
      const { error: resetError } = await withPortalTimeout(
        supabase.auth.resetPasswordForEmail(nextEmail, {
          redirectTo: getAuthRedirectUrl()
        }),
        'O envio do link demorou mais do que o esperado. Verifica a ligação e tenta novamente.'
      );

      if (resetError) {
        setError(resetError.message);
        return;
      }

      setResetMessage('Se existir uma conta ativa com este email, receberás um link de recuperação para definir uma nova password.');
    } catch (resetError: any) {
      setError(resetError?.message ?? 'Não foi possível enviar o link de recuperação.');
    } finally {
      setSubmitAction(null);
    }
  };

  const handlePasswordReset = async () => {
    setSubmitAction('password-reset');
    setError(null);
    setResetMessage(null);

    try {
      if (resetPassword.length < 6) {
        setError('A nova password deve ter pelo menos 6 caracteres.');
        return;
      }

      if (resetPassword !== resetPasswordConfirm) {
        setError('A confirmação da password não coincide.');
        return;
      }

      const { error: updateError } = await withPortalTimeout(
        supabase.auth.updateUser({
          password: resetPassword
        }),
        'A atualização da password demorou mais do que o esperado. Verifica a ligação e tenta novamente.'
      );

      if (updateError) {
        setError(updateError.message);
        return;
      }

      if (typeof window !== 'undefined') {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }

      await supabase.auth.signOut();
      setRecoveryPending(false);
      setIsRecoveryFlow(false);
      setResetPassword('');
      setResetPasswordConfirm('');
      setPassword('');
      setResetMessage('Password atualizada com sucesso. Já podes entrar com a nova password.');
    } catch (resetError: any) {
      setError(resetError?.message ?? 'Não foi possível atualizar a password.');
    } finally {
      setSubmitAction(null);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setRecoveryPending(false);
    setAccess(null);
    setEmail('');
    setPassword('');
    setError(null);
  };

  const handleReturnHome = async () => {
    await supabase.auth.signOut();
    setRecoveryPending(false);
    setAccess(null);
    setError(null);
    navigate('/');
  };

  const handleRetryHere = async () => {
    await supabase.auth.signOut();
    setRecoveryPending(false);
    setAccess(null);
    setError(null);
    setIsRecoveryFlow(false);
    setPassword('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setClientExists(true);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-red-500 p-6">
        <div className="rounded-[28px] border border-border bg-card p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <AqlVisionLoader
              compact
              label="A validar acesso"
              detail="A preparar o portal de configuração."
              className="items-start text-left"
            />
          </div>
        </div>
      </div>
    );
  }

  if (access) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <style>
          {`
            @keyframes aqlPortalReveal {
              0% {
                opacity: 0;
                transform: translateY(18px) scale(0.985);
                filter: blur(10px);
              }
              100% {
                opacity: 1;
                transform: translateY(0) scale(1);
                filter: blur(0);
              }
            }

            @keyframes aqlPortalMarkPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 16px 36px -24px rgba(56, 189, 248, 0.48); }
              50% { transform: scale(1.04); box-shadow: 0 22px 46px -24px rgba(56, 189, 248, 0.65); }
            }
          `}
        </style>
        <header className="border-b border-border bg-card/95 backdrop-blur">
          <div className="aql-adaptive-shell aql-config-shell flex w-full items-center justify-between gap-4 px-4 py-3 sm:px-5 lg:px-6 xl:px-8 2xl:px-10" style={{ animation: 'aqlPortalReveal 700ms cubic-bezier(0.22, 1, 0.36, 1) both' }}>
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-[#0f1a2a] p-2 text-white shadow-sm"
                style={{ animation: 'aqlPortalMarkPulse 5.2s ease-in-out infinite' }}
              >
                <AqlVisionMark className="h-full w-full object-contain" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">AQL Vision</p>
                <h1 className="text-lg font-semibold text-foreground">{title}</h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden rounded-2xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground/80 dark:text-muted-foreground sm:block">
                {access.email}
              </div>
              <Button variant="outline" onClick={handleLogout} className="inline-flex items-center gap-2">
                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                Sair
              </Button>
            </div>
          </div>
        </header>

        <main className="aql-adaptive-shell aql-config-shell flex min-h-0 flex-1 w-full p-0" style={{ animation: 'aqlPortalReveal 760ms cubic-bezier(0.22, 1, 0.36, 1) 90ms both' }}>
          <ConfigurationModule
            accessMode={access.mode}
            clientSlug={access.clientSlug}
            projectSlug={projectSlug}
            portalAccess={access}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-5 sm:px-6">
      <style>
        {`
          @keyframes aqlPortalHeroIn {
            0% {
              opacity: 0;
              transform: translateY(22px) scale(0.98);
              filter: blur(12px);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }

          @keyframes aqlPortalGlow {
            0%, 100% { opacity: 0.28; transform: scale(0.94); }
            50% { opacity: 0.7; transform: scale(1.04); }
          }

          @keyframes aqlPortalBadge {
            0% {
              opacity: 0;
              transform: translateY(-14px) scale(0.92);
              filter: blur(8px);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
              filter: blur(0);
            }
          }
        `}
      </style>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.14),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.12),transparent_28%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/30 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-cyan-500/[0.03]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-orange-500/[0.03]" />
      <div className="w-full max-w-2xl rounded-[24px] border border-border bg-card px-5 py-6 shadow-sm sm:px-8 lg:px-9 lg:py-7" style={{ animation: 'aqlPortalHeroIn 900ms cubic-bezier(0.22, 1, 0.36, 1) both' }}>
        <div className="mb-6 text-center">
          <div
            className="relative mx-auto mb-5 w-fit rounded-[24px] border border-slate-200/80 bg-white/90 px-4 py-3 shadow-[0_22px_60px_-38px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[#0f1a2a]/90"
            style={{ animation: 'aqlPortalBadge 780ms cubic-bezier(0.22, 1, 0.36, 1) 90ms both' }}
          >
            <div className="absolute inset-[-12%] rounded-[30px] bg-[radial-gradient(circle,rgba(56,189,248,0.16),rgba(20,184,166,0.08),transparent_72%)] blur-2xl dark:bg-[radial-gradient(circle,rgba(56,189,248,0.22),rgba(249,115,22,0.10),transparent_72%)]" style={{ animation: 'aqlPortalGlow 5.2s ease-in-out infinite' }} />
            <div className="relative flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[18px] border border-sky-100 bg-sky-50 p-2 text-sky-600 shadow-inner dark:border-white/10 dark:bg-white/[0.03] dark:text-white sm:h-16 sm:w-16">
                <AqlVisionMark className="h-full w-full object-contain" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#28358b] p-2 shadow-sm sm:h-16 sm:w-16">
                <AqlVerticalLogo className="h-full w-full object-contain" />
              </div>
            </div>
          </div>
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/80">AQL Vision Config</p>
          <h1 className="mx-auto mt-2 max-w-2xl text-[clamp(1.75rem,4vw,2.75rem)] font-semibold leading-tight text-foreground">{title}</h1>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
            {resolvedMode === 'admin'
              ? 'Acesso global à configuração da plataforma.'
              : 'Acesso limitado à configuração do cliente deste URL.'}
          </p>
        </div>

        {!clientExists ? (
          <div className="space-y-5">
            <div className="rounded-[18px] border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              O cliente deste endpoint ainda não existe na configuração.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Button
                variant="outline"
                onClick={handleRetryHere}
                className="inline-flex rounded-[18px] py-5 text-sm font-semibold"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Tentar novamente
              </Button>
              <Button
                onClick={handleReturnHome}
                className="inline-flex rounded-[18px] py-5 text-sm font-semibold"
              >
                <Home className="mr-2 h-4 w-4" />
                Voltar ao início
              </Button>
            </div>
          </div>
        ) : isRecoveryFlow ? (
          <div className="mx-auto max-w-lg space-y-4">
            <div className="rounded-[16px] border border-border bg-muted/20 px-4 py-3 text-sm text-foreground/80 dark:text-muted-foreground">
              O link de convite ou recuperação foi aceite. Define agora a tua nova password para entrar no portal.
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Nova password</label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(event) => setResetPassword(event.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full rounded-[16px] border border-border bg-muted/30 py-3 pl-11 pr-4 text-base text-foreground outline-none transition-colors focus:border-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Confirmar password</label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(event) => setResetPasswordConfirm(event.target.value)}
                  placeholder="Repete a nova password"
                  className="w-full rounded-[16px] border border-border bg-muted/30 py-3 pl-11 pr-4 text-base text-foreground outline-none transition-colors focus:border-primary"
                />
              </div>
            </div>

            {error ? (
              <div className="rounded-[18px] border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            ) : null}

            {resetMessage ? (
              <div className="rounded-[18px] border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                {resetMessage}
              </div>
            ) : null}

            <Button onClick={handlePasswordReset} disabled={submitting} className="w-full rounded-[16px] py-5 text-base font-semibold">
              {submitAction === 'password-reset' ? 'A atualizar...' : 'Guardar nova password'}
            </Button>

            <Button
              variant="outline"
              onClick={handleRetryHere}
              disabled={submitting}
              className="w-full rounded-[16px] py-4 text-sm font-semibold"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao login
            </Button>
          </div>
        ) : (
          <div className="mx-auto max-w-lg space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Email
              </label>
              <div className="relative">
                <UserRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={resolvedMode === 'admin' ? 'admin@aqlvision.pt' : 'email do utilizador'}
                  className="w-full rounded-[16px] border border-border bg-muted/30 py-3 pl-11 pr-4 text-base text-foreground outline-none transition-colors focus:border-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="123"
                  className="w-full rounded-[16px] border border-border bg-muted/30 py-3 pl-11 pr-4 text-base text-foreground outline-none transition-colors focus:border-primary"
                />
              </div>
            </div>

            {error ? (
              <div className="rounded-[18px] border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            ) : null}

            {resetMessage ? (
              <div className="rounded-[18px] border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                {resetMessage}
              </div>
            ) : null}

            <Button onClick={handleLogin} disabled={submitting} className="w-full rounded-[16px] py-5 text-base font-semibold">
              {submitAction === 'login' ? 'A entrar...' : 'Entrar na configuração'}
            </Button>

            <Button
              variant="outline"
              onClick={handleRequestPasswordReset}
              disabled={submitting}
              className="w-full rounded-[16px] py-5 text-base font-semibold"
            >
              {submitAction === 'reset-link' ? 'A enviar link...' : 'Receber link de recuperação'}
            </Button>

            <Button
              variant="ghost"
              onClick={handleReturnHome}
              disabled={submitting}
              className="w-full rounded-[16px] py-4 text-sm font-semibold text-muted-foreground/90 hover:text-foreground"
            >
              <Home className="mr-2 h-4 w-4" />
              Voltar ao início
            </Button>

          </div>
        )}
      </div>
    </div>
  );
}
